import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit2, Save, X, Plus, ChevronDown, ChevronRight } from 'lucide-react';
import axios from 'axios';
import DynamicFormRenderer from '../Forms/DynamicFormRenderer';
import ItemTypeSelector from './project-items/ItemTypeSelector';
import ClientTaskForm from './project-items/client-tasks/ClientTaskForm';
import MeetingForm from './project-items/meetings/MeetingForm';
import ApprovalForm from './project-items/approvals/ApprovalForm';
import ContentItemForm from './project-items/content/ContentItemForm';
import ReviewItemForm from './project-items/reviews/ReviewItemForm';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://content-review-api.ryantoth2410.workers.dev/api';

interface Project {
  id: number;
  name: string;
  description: string;
  client_name: string;
  services: string;
  notes: string;
  target_end_date: string;
  owner: string;
  priority: string;
  status: string;
  long_term_deadline: string;
  is_proposal: boolean;
  phases?: Phase[];
}

interface Phase {
  id: number;
  name: string;
  description: string;
  status: string;
  order_index: number;
  tasks: Task[];
}

interface Task {
  id: number;
  name: string;
  description: string;
  status: string;
  assigned_to: string;
  assigned_to_type: string;
  due_date: string;
  priority: string;
  estimated_hours: number;
  actual_hours: number;
  step_type_id: number | null;
  step_type_name: string;
  step_type: string;
  step_type_color: string;
  step_type_icon: string;
}

interface Contact {
  id: number;
  project_id: number;
  name: string;
  email: string | null;
  phone: string | null;
  role: string | null;
  company: string | null;
  notes: string | null;
  is_primary: boolean;
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingProject, setEditingProject] = useState(false);
  const [editedProject, setEditedProject] = useState<any>({});
  const [selectedPhaseId, setSelectedPhaseId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'tasks' | 'contacts'>('overview');
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [showAddContact, setShowAddContact] = useState(false);
  const [newContact, setNewContact] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    company: '',
    notes: '',
    is_primary: false
  });
  const [showAddPhase, setShowAddPhase] = useState(false);
  const [newPhase, setNewPhase] = useState({ name: '', description: '', status: 'pending' });
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTask, setNewTask] = useState({
    name: '',
    description: '',
    status: 'todo',
    priority: 'medium',
    assigned_to: '',
    assigned_to_type: 'team',
    due_date: '',
    estimated_hours: 0,
    step_type_id: null as number | null
  });
  const [stepTypes, setStepTypes] = useState<any[]>([]);
  const [stepTypeFields, setStepTypeFields] = useState<any[]>([]);
  const [formFieldValues, setFormFieldValues] = useState<Record<string, any>>({});
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showCategorySelector, setShowCategorySelector] = useState(false);

  // Memoized grouped step types for dropdown
  const groupedStepTypes = useMemo(() => {
    return stepTypes.reduce((groups: Record<string, any[]>, stepType: any) => {
      const category = stepType.type;
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(stepType);
      return groups;
    }, {});
  }, [stepTypes]);

  useEffect(() => {
    loadProject();
    loadContacts();
    loadStepTypes();
  }, [id]);

  const loadStepTypes = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/step-types`);
      setStepTypes(response.data.step_types || []);
    } catch (error) {
      console.error('Failed to load step types:', error);
    }
  };

  const loadStepTypeFields = async (stepTypeId: number) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/step-types/${stepTypeId}/fields`);
      setStepTypeFields(response.data.fields || []);
    } catch (error) {
      console.error('Failed to load step type fields:', error);
      setStepTypeFields([]);
    }
  };

  const loadProject = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/projects-v2/${id}`);
      setProject(response.data.project);
      setEditedProject(response.data.project);
      // Auto-select first phase if available
      if (response.data.project.phases && response.data.project.phases.length > 0) {
        setSelectedPhaseId(response.data.project.phases[0].id);
      }
    } catch (error) {
      console.error('Failed to load project:', error);
      alert('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const loadContacts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/contacts/projects/${id}/contacts`);
      setContacts(response.data);
    } catch (error) {
      console.error('Failed to load contacts:', error);
    }
  };

  const saveProject = async () => {
    try {
      await axios.put(`${API_BASE_URL}/projects-v2/${id}`, editedProject);
      setProject(editedProject);
      setEditingProject(false);
    } catch (error) {
      console.error('Failed to save project:', error);
      alert('Failed to save project');
    }
  };

  const addContact = async () => {
    try {
      await axios.post(`${API_BASE_URL}/contacts/projects/${id}/contacts`, newContact);
      setShowAddContact(false);
      setNewContact({ name: '', email: '', phone: '', role: '', company: '', notes: '', is_primary: false });
      loadContacts();
    } catch (error) {
      console.error('Failed to add contact:', error);
      alert('Failed to add contact');
    }
  };

  const deleteContact = async (contactId: number) => {
    if (!confirm('Are you sure you want to delete this contact?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/contacts/project-contacts/${contactId}`);
      loadContacts();
    } catch (error) {
      console.error('Failed to delete contact:', error);
      alert('Failed to delete contact');
    }
  };

  const addPhase = async () => {
    try {
      await axios.post(`${API_BASE_URL}/projects-v2/${id}/phases`, newPhase);
      setShowAddPhase(false);
      setNewPhase({ name: '', description: '', status: 'pending' });
      loadProject();
    } catch (error) {
      console.error('Failed to add phase:', error);
      alert('Failed to add phase');
    }
  };

  const addTask = async () => {
    if (!newTask.step_type_id) {
      alert('Please select a task type');
      return;
    }
    if (!selectedPhaseId) {
      alert('Please select a phase');
      return;
    }

    try {
      const taskData = {
        ...newTask,
        config: JSON.stringify(formFieldValues) // Store form field values in config
      };
      await axios.post(`${API_BASE_URL}/projects-v2/phases/${selectedPhaseId}/tasks`, taskData);
      setShowAddTask(false);
      setNewTask({
        name: '',
        description: '',
        status: 'todo',
        priority: 'medium',
        assigned_to: '',
        assigned_to_type: 'team',
        due_date: '',
        estimated_hours: 0,
        step_type_id: null
      });
      setStepTypeFields([]);
      setFormFieldValues({});
      loadProject();
    } catch (error) {
      console.error('Failed to add task:', error);
      alert('Failed to add task');
    }
  };

  const addTaskFromCategory = async (taskData: any) => {
    if (!selectedPhaseId) {
      alert('Please select a phase');
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/projects-v2/phases/${selectedPhaseId}/tasks`, taskData);
      setShowAddTask(false);
      setSelectedCategory(null);
      loadProject();
    } catch (error) {
      console.error('Failed to add task:', error);
      alert('Failed to add task');
    }
  };

  const selectedPhase = project?.phases?.find(p => p.id === selectedPhaseId);

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'active': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading project...</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-gray-500">Project not found</p>
          <button
            onClick={() => navigate('/dashboard/projects')}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      {/* Left Sidebar - Phases */}
      <div className="w-64 bg-white border-r border-gray-200 flex flex-col overflow-hidden">
        {/* Sidebar Header */}
        <div className="p-4 border-b border-gray-200">
          <button
            onClick={() => navigate('/dashboard/projects')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-3 text-sm"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Projects
          </button>
          <h2 className="font-bold text-gray-900 truncate">{project.name}</h2>
          <p className="text-sm text-gray-600 truncate">{project.client_name}</p>
        </div>

        {/* Phases List */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Phases</h3>
            <button
              onClick={() => setShowAddPhase(true)}
              className="text-blue-600 hover:text-blue-700"
              title="Add Phase"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          {project.phases && project.phases.length > 0 ? (
            <div className="space-y-1">
              {project.phases.map((phase) => (
                <button
                  key={phase.id}
                  onClick={() => {
                    setSelectedPhaseId(phase.id);
                    setActiveTab('tasks');
                  }}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition ${
                    selectedPhaseId === phase.id
                      ? 'bg-blue-100 text-blue-900 font-medium'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate">📋 {phase.name}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded border whitespace-nowrap ${getStatusColor(phase.status)}`}>
                      {phase.status}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 py-4">No phases yet</p>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header with Edit Button */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              {editingProject ? (
                <input
                  type="text"
                  value={editedProject.name}
                  onChange={(e) => setEditedProject({ ...editedProject, name: e.target.value })}
                  className="text-xl font-bold border-b-2 border-blue-500 focus:outline-none w-full"
                />
              ) : (
                <div>
                  <h1 className="text-xl font-bold text-gray-900">{project.name}</h1>
                  {selectedPhase && <p className="text-sm text-gray-600 mt-1">Phase: {selectedPhase.name}</p>}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {editingProject ? (
                <>
                  <button
                    onClick={saveProject}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setEditingProject(false);
                      setEditedProject(project);
                    }}
                    className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setEditingProject(true)}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit Project
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white border-b border-gray-200 px-6">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-3 border-b-2 font-medium text-sm transition ${
                activeTab === 'overview'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('tasks')}
              className={`px-4 py-3 border-b-2 font-medium text-sm transition ${
                activeTab === 'tasks'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
              disabled={!selectedPhase}
            >
              Tasks {selectedPhase && `(${selectedPhase.tasks?.length || 0})`}
            </button>
            <button
              onClick={() => setActiveTab('contacts')}
              className={`px-4 py-3 border-b-2 font-medium text-sm transition ${
                activeTab === 'contacts'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Contacts ({contacts.length})
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
          {activeTab === 'overview' && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Project Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Services</label>
                  {editingProject ? (
                    <input
                      type="text"
                      value={editedProject.services || ''}
                      onChange={(e) => setEditedProject({ ...editedProject, services: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  ) : (
                    <p className="text-gray-900">{project.services || '-'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
                  {editingProject ? (
                    <input
                      type="text"
                      value={editedProject.owner || ''}
                      onChange={(e) => setEditedProject({ ...editedProject, owner: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  ) : (
                    <p className="text-gray-900">{project.owner || '-'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  {editingProject ? (
                    <select
                      value={editedProject.priority}
                      onChange={(e) => setEditedProject({ ...editedProject, priority: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  ) : (
                    <p className="text-gray-900 capitalize">{project.priority}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  {editingProject ? (
                    <select
                      value={editedProject.status}
                      onChange={(e) => setEditedProject({ ...editedProject, status: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="planning">Planning</option>
                      <option value="active">Active</option>
                      <option value="on_hold">On Hold</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  ) : (
                    <p className="text-gray-900 capitalize">{project.status.replace('_', ' ')}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  {editingProject ? (
                    <input
                      type="date"
                      value={editedProject.target_end_date?.split('T')[0] || ''}
                      onChange={(e) => setEditedProject({ ...editedProject, target_end_date: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  ) : (
                    <p className="text-gray-900">{project.target_end_date ? new Date(project.target_end_date).toLocaleDateString() : '-'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Long Term Deadline</label>
                  {editingProject ? (
                    <input
                      type="date"
                      value={editedProject.long_term_deadline?.split('T')[0] || ''}
                      onChange={(e) => setEditedProject({ ...editedProject, long_term_deadline: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  ) : (
                    <p className="text-gray-900">{project.long_term_deadline ? new Date(project.long_term_deadline).toLocaleDateString() : '-'}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  {editingProject ? (
                    <textarea
                      value={editedProject.notes || ''}
                      onChange={(e) => setEditedProject({ ...editedProject, notes: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  ) : (
                    <p className="text-gray-900">{project.notes || '-'}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'tasks' && selectedPhase && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Tasks in {selectedPhase.name}</h2>
                <button
                  onClick={() => {
                    setShowCategorySelector(true);
                  }}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Task
                </button>
              </div>
              {selectedPhase.tasks && selectedPhase.tasks.length > 0 ? (
                <div className="space-y-2">
                  {selectedPhase.tasks.map((task) => (
                    <div key={task.id} className="bg-white rounded-lg shadow p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-gray-900">{task.name}</h3>
                            {task.step_type_name && (
                              <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                                {task.step_type_name}
                              </span>
                            )}
                          </div>
                          {task.description && <p className="text-sm text-gray-500 mt-1">{task.description}</p>}
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-600">
                            {task.assigned_to && (
                              <span>👤 {task.assigned_to} ({task.assigned_to_type})</span>
                            )}
                            {task.due_date && (
                              <span>📅 {new Date(task.due_date).toLocaleDateString()}</span>
                            )}
                            {task.estimated_hours > 0 && (
                              <span>⏱️ {task.estimated_hours}h</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded text-xs border ${
                            task.status === 'completed' ? 'bg-green-100 text-green-700 border-green-200' :
                            task.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' :
                            task.status === 'blocked' ? 'bg-red-100 text-red-700 border-red-200' :
                            'bg-gray-100 text-gray-600 border-gray-200'
                          }`}>
                            {task.status}
                          </span>
                          {task.priority && (
                            <span className={`px-2 py-1 rounded text-xs border ${
                              task.priority === 'urgent' ? 'bg-red-100 text-red-700 border-red-200' :
                              task.priority === 'high' ? 'bg-orange-100 text-orange-700 border-orange-200' :
                              'bg-gray-100 text-gray-600 border-gray-200'
                            }`}>
                              {task.priority}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500 bg-white rounded-lg">
                  <p>No tasks in this phase yet</p>
                  <button
                    onClick={() => setShowCategorySelector(true)}
                    className="mt-4 text-blue-600 hover:text-blue-700"
                  >
                    Add your first task
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'tasks' && !selectedPhase && (
            <div className="text-center py-12 text-gray-500 bg-white rounded-lg">
              <p>Select a phase to view tasks</p>
            </div>
          )}

          {activeTab === 'contacts' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Project Contacts</h2>
                <button
                  onClick={() => setShowAddContact(true)}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Contact
                </button>
              </div>

              {showAddContact && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="font-semibold mb-4">Add New Contact</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                      <input
                        type="text"
                        value={newContact.name}
                        onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={newContact.email}
                        onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        type="tel"
                        value={newContact.phone}
                        onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                      <input
                        type="text"
                        value={newContact.role}
                        onChange={(e) => setNewContact({ ...newContact, role: e.target.value })}
                        placeholder="e.g., Project Manager, Developer"
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                      <input
                        type="text"
                        value={newContact.company}
                        onChange={(e) => setNewContact({ ...newContact, company: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="flex items-center gap-2 mt-6">
                        <input
                          type="checkbox"
                          checked={newContact.is_primary}
                          onChange={(e) => setNewContact({ ...newContact, is_primary: e.target.checked })}
                          className="rounded"
                        />
                        <span className="text-sm text-gray-700">Primary Contact</span>
                      </label>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                      <textarea
                        value={newContact.notes}
                        onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={addContact}
                      disabled={!newContact.name}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      Save Contact
                    </button>
                    <button
                      onClick={() => {
                        setShowAddContact(false);
                        setNewContact({ name: '', email: '', phone: '', role: '', company: '', notes: '', is_primary: false });
                      }}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {contacts.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {contacts.map((contact) => (
                    <div key={contact.id} className="bg-white rounded-lg shadow p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-gray-900">{contact.name}</h3>
                            {contact.is_primary && (
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">Primary</span>
                            )}
                          </div>
                          {contact.role && <p className="text-sm text-gray-600 mt-1">{contact.role}</p>}
                          {contact.company && <p className="text-sm text-gray-500">{contact.company}</p>}
                          <div className="mt-2 space-y-1">
                            {contact.email && (
                              <p className="text-sm text-gray-600">✉️ {contact.email}</p>
                            )}
                            {contact.phone && (
                              <p className="text-sm text-gray-600">📞 {contact.phone}</p>
                            )}
                          </div>
                          {contact.notes && (
                            <p className="text-xs text-gray-500 mt-2 italic">{contact.notes}</p>
                          )}
                        </div>
                        <button
                          onClick={() => deleteContact(contact.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500 bg-white rounded-lg">
                  <p>No contacts added yet</p>
                  <button
                    onClick={() => setShowAddContact(true)}
                    className="mt-4 text-blue-600 hover:text-blue-700"
                  >
                    Add your first contact
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add Phase Modal */}
      {showAddPhase && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Add New Phase</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={newPhase.name}
                  onChange={(e) => setNewPhase({ ...newPhase, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., Discovery Phase"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newPhase.description}
                  onChange={(e) => setNewPhase({ ...newPhase, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={newPhase.status}
                  onChange={(e) => setNewPhase({ ...newPhase, status: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="pending">Pending</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                onClick={addPhase}
                disabled={!newPhase.name}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                Add Phase
              </button>
              <button
                onClick={() => {
                  setShowAddPhase(false);
                  setNewPhase({ name: '', description: '', status: 'pending' });
                }}
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Task Modal */}
      {showAddTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Add New Task</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Task Type *</label>
                <select
                  value={newTask.step_type_id || ''}
                  onChange={(e) => {
                    const stepTypeId = parseInt(e.target.value);
                    setNewTask({ ...newTask, step_type_id: stepTypeId });
                    if (stepTypeId) {
                      loadStepTypeFields(stepTypeId);
                    } else {
                      setStepTypeFields([]);
                      setFormFieldValues({});
                    }
                  }}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Select a task type...</option>
                  {Object.entries(groupedStepTypes).map(([category, types]: [string, any[]]) => (
                    <optgroup
                      key={category}
                      label={category.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    >
                      {types.map((type: any) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={newTask.name}
                  onChange={(e) => setNewTask({ ...newTask, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., Design homepage mockup"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              {/* Dynamic Form Fields */}
              {stepTypeFields.length > 0 && (
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium text-gray-900 mb-3">Form Fields</h4>
                  <DynamicFormRenderer
                    fields={stepTypeFields}
                    values={formFieldValues}
                    onChange={(fieldName, value) => {
                      setFormFieldValues({ ...formFieldValues, [fieldName]: value });
                    }}
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={newTask.status}
                    onChange={(e) => setNewTask({ ...newTask, status: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="todo">To Do</option>
                    <option value="in_progress">In Progress</option>
                    <option value="waiting">Waiting</option>
                    <option value="blocked">Blocked</option>
                    <option value="completed">Completed</option>
                    <option value="skipped">Skipped</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={newTask.priority}
                    onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                  <input
                    type="text"
                    value={newTask.assigned_to}
                    onChange={(e) => setNewTask({ ...newTask, assigned_to: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Person name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To Type</label>
                  <select
                    value={newTask.assigned_to_type}
                    onChange={(e) => setNewTask({ ...newTask, assigned_to_type: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="team">Team</option>
                    <option value="client">Client</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={newTask.due_date}
                    onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Hours</label>
                  <input
                    type="number"
                    value={newTask.estimated_hours}
                    onChange={(e) => setNewTask({ ...newTask, estimated_hours: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="0"
                    step="0.5"
                  />
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                onClick={addTask}
                disabled={!newTask.name}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                Add Task
              </button>
              <button
                onClick={() => {
                  setShowAddTask(false);
                  setNewTask({
                    name: '',
                    description: '',
                    status: 'todo',
                    priority: 'medium',
                    assigned_to: '',
                    assigned_to_type: 'team',
                    due_date: '',
                    estimated_hours: 0,
                    step_type_id: null
                  });
                  setStepTypeFields([]);
                  setFormFieldValues({});
                }}
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Selector Modal */}
      {showCategorySelector && (
        <ItemTypeSelector
          onSelectCategory={(category) => {
            setSelectedCategory(category);
            setShowCategorySelector(false);
            setShowAddTask(true);
          }}
          onCancel={() => {
            setShowCategorySelector(false);
            setSelectedCategory(null);
          }}
        />
      )}

      {/* Category-Specific Forms */}
      {showAddTask && selectedCategory === 'client_task' && (
        <ClientTaskForm
          stepId={0}
          stepTypeId={stepTypes.find(st => st.type === 'client_task')?.id || 0}
          onSubmit={async (data) => {
            await addTaskFromCategory(data);
          }}
          onCancel={() => {
            setShowAddTask(false);
            setSelectedCategory(null);
          }}
        />
      )}

      {showAddTask && selectedCategory === 'meeting' && (
        <MeetingForm
          stepId={0}
          stepTypeId={stepTypes.find(st => st.type === 'meeting')?.id || 0}
          onSubmit={async (data) => {
            await addTaskFromCategory(data);
          }}
          onCancel={() => {
            setShowAddTask(false);
            setSelectedCategory(null);
          }}
        />
      )}

      {showAddTask && selectedCategory === 'approval_form' && (
        <ApprovalForm
          stepId={0}
          stepTypeId={stepTypes.find(st => st.type === 'approval_form')?.id || 0}
          onSubmit={async (data) => {
            await addTaskFromCategory(data);
          }}
          onCancel={() => {
            setShowAddTask(false);
            setSelectedCategory(null);
          }}
        />
      )}

      {showAddTask && selectedCategory === 'content' && (
        <ContentItemForm
          stepId={0}
          stepTypeId={stepTypes.find(st => st.type === 'content')?.id || 0}
          clientId={project?.client_id}
          onSubmit={async (data) => {
            await addTaskFromCategory(data);
          }}
          onCancel={() => {
            setShowAddTask(false);
            setSelectedCategory(null);
          }}
        />
      )}

      {showAddTask && selectedCategory === 'review' && (
        <ReviewItemForm
          stepId={0}
          stepTypeId={stepTypes.find(st => st.type === 'review')?.id || 0}
          onSubmit={async (data) => {
            await addTaskFromCategory(data);
          }}
          onCancel={() => {
            setShowAddTask(false);
            setSelectedCategory(null);
          }}
        />
      )}

      {/* Fallback: Show old generic modal for other categories */}
      {showAddTask && !selectedCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Create Task</h3>
            <p className="text-gray-600">Please select a task type first</p>
            <button
              onClick={() => {
                setShowAddTask(false);
                setShowCategorySelector(true);
              }}
              className="mt-4 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Choose Task Type
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
